import React from "react";
import { RouteObject } from "react-router-dom";
import { PluginModule, NavigationItem } from "../../plugins/types";

// Import pages from the pages directory (new structure)
import Dashboard from "./pages/Dashboard";
// import DocumentsRegeneration from "./pages/DocumentsRegeneration"; // TEMP: Disabled - depends on deleted components
import KnowledgeBase from "./pages/KnowledgeBase";
import KnowledgeBaseDocuments from "./pages/KnowledgeBaseDocuments";
import KnowledgeBasePrompts from "./pages/KnowledgeBasePrompts";
import PolicyDocuments from "./pages/PolicyDocuments";
import QuickCompare from "./pages/QuickCompare";
import ParseDoc from "./pages/ParseDoc";
import NewAudit from "./pages/NewAudit";
import AuditWorkflowSelection from "./pages/AuditWorkflowSelection";
import BoostPolicyFlow from "./pages/BoostPolicyFlow";
import BoostPolicyGenerating from "./pages/BoostPolicyGenerating";
import TemplateLibrary from "./pages/TemplateLibrary";
import ScanAndReview from "./pages/ScanAndReview";
import EditAndApprove from "./pages/EditAndApprove";
import PreviewExport from "./pages/PreviewExport";
import PolicyDetailsPage from "./pages/create-policy/PolicyDetailsPage";
import PolicyGeneratePage from "./pages/create-policy/PolicyGeneratePage";
import PolicyEditPage from "./pages/create-policy/PolicyEditPage";
import { PostOnboardingChoice } from "./pages/PostOnboardingChoice";
import { OrganisationProfilePage } from "./pages/OrganisationProfilePage";
import { OrgMemoryManagerPage } from "./pages/OrgMemoryManagerPage";
import { OrgAdminPortal } from "../../foundation-overrides/pages/OrgAdminPortal";
import { ContextCategoriesPage } from "./pages/admin/ContextCategoriesPage";
import { ContextCategoryEditPage } from "./pages/admin/ContextCategoryEditPage";
import { DocumentRegister } from "./pages/DocumentRegister";
import { PolicyApprovals } from "./pages/PolicyApprovals";

// Shared ScopingAI pages — imported and configured via props for Revisr context
import BrandYourProposal from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/pages/BrandYourProposal";
import BrandedProposalGenerating from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/pages/BrandedProposalGenerating";
import { BrandPolicyEditorWrapper } from "../../template-overrides/pages/BrandPolicyEditorWrapper";
import DocumentsLibrary from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/pages/DocumentsLibrary";
import TemplateCreation from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/pages/TemplateCreation";
import ProposalTemplateEditor from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/pages/ProposalTemplateEditor";
import RevisrLayout from "./components/RevisrLayout";

// Layout variant with mobile top bar hidden — for editor/preview pages that have their own toolbar
const RevisrLayoutNoBar = ({ children }: { children: React.ReactNode }) =>
  React.createElement(RevisrLayout, { hideMobileBar: true }, children);
import type { BrandWorkflowConfig, TemplateEditorConfig } from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/types/brandWorkflowConfig";
import type { DocumentsLibraryConfig } from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/pages/DocumentsLibrary";
import { getAllTemplates } from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/services/documentLibraryService";
import type { TemplateServiceConfig } from "../../altzero_plugins/features/scopingai/scopingAi/features/scopingAi/services/documentLibraryService";
import { supabaseRevisr } from "./utils/supabaseRevisrClient";
import { getUserOrganizations } from "../../altzero_plugins/crmfoundation/altzero_main/altzero_plugins/foundation/altzero_base/base/services/organizationService";
import { BrandingService } from "../../altzero_plugins/crmfoundation/altzero_main/altzero_plugins/foundation/altzero_base/base/services/brandingService";
import { getBoostPolicyById, fetchSavedContent, fetchEditorContent, loadPolicyTemplateById } from "./services/boostPolicyService";
import { contentPagesToMarkdown } from "./utils/platePagesToMarkdown";
import { saveContent } from "./services/policyGenerationService";
import { mergeSessionResultContent } from "./utils/boostPolicySession";
import { getActiveWorkspaceId } from "../../header/activeWorkspace";

// Tells the shared template service to query revisr.policy_templates + "templates" bucket
const REVISR_TEMPLATE_SVC: TemplateServiceConfig = {
  supabaseClient: supabaseRevisr as unknown as TemplateServiceConfig["supabaseClient"],
  tableName: "policy_templates",
  bucketName: "templates",
};

// Revisr-specific config for the Templates page
const REVISR_TEMPLATES_CONFIG: DocumentsLibraryConfig = {
  layout: RevisrLayout,
  routes: {
    back: "/revisr/dashboard",
    backLabel: "Back to Dashboard",
    templateCreate: "/revisr/templates/create",
    templateEditorNew: "/revisr/template-editor/new",
    templateEditorById: (id: string) => `/revisr/template-editor/${id}`,
    documentNew: "/revisr/templates",
  },
  labels: {
    title: "Policy Templates",
    subtitle: "Manage and organize your policy templates",
  },
  serviceConfig: REVISR_TEMPLATE_SVC,
};

// Shared config for template creation/editing pages within Revisr
const REVISR_TEMPLATE_EDITOR_CONFIG: TemplateEditorConfig = {
  layout: RevisrLayoutNoBar,
  routes: {
    templatesList: "/revisr/templates",
    documentNew: "/revisr/templates",
  },
  serviceConfig: REVISR_TEMPLATE_SVC,
};

// Shared config for all Brand Your Policy pages
const REVISR_BRAND_CONFIG: BrandWorkflowConfig = {
  layout: RevisrLayoutNoBar,
  routes: {
    back: "/revisr/audit-workflow-selection",
    brandForm: "/revisr/brand-policy/generating",
    generating: "/revisr/brand-policy/generating",
    editor: "/revisr/brand-policy/editor",
    preview: "/revisr/brand-policy/preview",
    afterSave: "/revisr/policies",
  },
  session: {
    dataKey: "revisr_brand_policy_data",
    resultKey: "revisr_brand_policy_result",
  },
  api: {
    streamUrl: `${(typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || ""}/api/revisr/brand-policy/stream`,
    saveUrl: `${(typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || ""}/api/revisr/brand-policy/save`,
    apiKey: "revisr",
  },
  labels: {
    noun: "Policy",
    title: "Brand Your Policy",
    subtitle: "Pick a template, paste your content, and let AI brand it for you",
    generateButton: "Generate Branded Policy",
    saveButton: "Save Policy",
    defaultDocTitle: "Branded Policy",
  },
  accentColor: "purple",
  loadTemplates: (userId: string) => getAllTemplates(userId, REVISR_TEMPLATE_SVC),
  loadBranding: async (_user: { id: string }) => {
    try {
      const orgData = await getUserOrganizations();
      const org = orgData.organizations[0];
      if (!org) return null;
      const branding = await BrandingService.getOrganizationBranding(org.id);
      return branding as unknown as Record<string, unknown> | null;
    } catch (err) {
      console.error("Failed to load branding for Revisr:", err);
      return null;
    }
  },
  loadFn: async (policyId: string) => {
    try {
      const record = await getBoostPolicyById(policyId);

      // Prefer exact editor state (branded pages) when the editor has saved before.
      const editorState = await fetchEditorContent(policyId).catch(() => null);

      // Classification stored in revisr_final_policies.metadata (written at generation
      // from the Q&A Tier 0 answer, refreshed from the editable footer on editor save).
      // Used to seed the footer when the editor JSON has none — keeps the per-document
      // classification on cross-tab / DB resume instead of falling back to "Public".
      const metadataClassification =
        (record.metadata?.classification as string | undefined) ?? 'Public';

      // Resolve template for metadata only on the editor-state path — the editor
      // renders editorState.pages directly, so resolvedPages substitution (applied
      // on the markdown path below) is intentionally not needed here.
      const templateIdEarly = record.audit_results?.selectedTemplateId;
      let selectedTemplateEarly: { id: string; pages: unknown[] | null; sectionNames: string[] } | null = null;
      if (templateIdEarly) {
        selectedTemplateEarly = await loadPolicyTemplateById(templateIdEarly);
      }

      if (editorState && Array.isArray(editorState.pages) && editorState.pages.length > 0) {
        return {
          sections: [],
          branding: (record.branding_data as Record<string, unknown>) ?? null,
          title: record.policy_name || "Policy",
          pages: editorState.pages,
          zoom: editorState.zoom ?? 1,
          selectedTemplate: selectedTemplateEarly,
          // Editor-saved footer wins; fall back to metadata classification when
          // the editor JSON predates the footer feature (or was saved without it).
          footer: {
            ...editorState.footer,
            classification: editorState.footer?.classification ?? metadataClassification,
          },
          includeCoverToc: editorState.includeCoverToc,
        };
      }

      const savedResult = await fetchSavedContent(policyId);
      const content = savedResult?.content ?? null;

      // Load template from DB using the template ID stored in audit_results.
      // If resolved pages were persisted during generation, use those instead
      // (they already have {{placeholders}} filled with actual values).
      const templateId = record.audit_results?.selectedTemplateId;
      let selectedTemplate: { id: string; pages: unknown[] | null; sectionNames: string[] } | null = null;
      if (templateId) {
        selectedTemplate = await loadPolicyTemplateById(templateId);
        // Override raw template pages with resolved pages if available
        if (savedResult?.resolvedPages && selectedTemplate) {
          selectedTemplate = { ...selectedTemplate, pages: savedResult.resolvedPages };
        }
      }

      if (!content) {
        return selectedTemplate
          ? { sections: [], branding: (record.branding_data as Record<string, unknown>) ?? null, title: record.policy_name || "Policy", selectedTemplate, footer: { classification: metadataClassification } }
          : null;
      }

      // Legacy: pre-migration-031 records stored editor JSON in policy_markdown_url.
      // New records keep editor JSON in editor_content_url (handled above), so this
      // JSON-detect branch only fires for policies last saved before that split.
      try {
        const parsed = JSON.parse(content) as { pages?: unknown[]; zoom?: number };
        if (Array.isArray(parsed.pages) && parsed.pages.length > 0) {
          return {
            sections: [],
            branding: (record.branding_data as Record<string, unknown>) ?? null,
            title: record.policy_name || "Policy",
            pages: parsed.pages,
            zoom: parsed.zoom ?? 1,
            selectedTemplate,
            footer: { classification: metadataClassification },
          };
        }
      } catch {
        // Not JSON — treat as markdown below
      }

      // Markdown content — split into sections
      const sections: { title: string; content: string }[] = [];
      const parts = content.split(/^## /m);
      for (const part of parts) {
        if (!part.trim()) continue;
        const newlineIdx = part.indexOf("\n");
        if (newlineIdx === -1) {
          sections.push({ title: part.trim(), content: "" });
        } else {
          sections.push({ title: part.slice(0, newlineIdx).trim(), content: part.slice(newlineIdx + 1).trim() });
        }
      }
      if (sections.length === 0 && content.trim()) {
        sections.push({ title: "Policy Content", content: content.trim() });
      }

      return {
        sections,
        branding: (record.branding_data as Record<string, unknown>) ?? null,
        title: record.policy_name || "Policy",
        fullContent: content,
        selectedTemplate,
        footer: { classification: metadataClassification },
      };
    } catch (err) {
      console.error("[Revisr loadFn] Failed to load policy from DB:", err);
      return null;
    }
  },
  saveFn: async ({ title, pages, zoom, userId, orgId, footer, includeCoverToc }) => {
    const apiUrl = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || "";
    // Pass existing policyId so the backend updates the existing record instead of inserting
    const urlParams = new URLSearchParams(window.location.search);
    const policyId = urlParams.get("id") || sessionStorage.getItem("boost_policy_record_id") || undefined;
    // Resolve orgId — must match the orgId used when the policy was created.
    // Priority: caller-supplied → active workspace (same source as createBoostPolicyDraft) → sessionStorage → API fallback
    let resolvedOrgId = orgId;
    if (!resolvedOrgId) {
      resolvedOrgId = getActiveWorkspaceId() ?? undefined;
    }
    if (!resolvedOrgId) {
      try {
        const storedData = sessionStorage.getItem("revisr_brand_policy_data");
        if (storedData) {
          const parsed = JSON.parse(storedData);
          resolvedOrgId = parsed?.orgId as string | undefined;
        }
      } catch { /* non-fatal */ }
    }
    if (!resolvedOrgId) {
      try {
        const orgData = await getUserOrganizations();
        resolvedOrgId = orgData.organizations[0]?.id;
      } catch { /* non-fatal */ }
    }
    const resp = await fetch(`${apiUrl}/api/revisr/brand-policy/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "revisr",
        "x-user-id": userId,
        ...(resolvedOrgId ? { "x-org-id": resolvedOrgId } : {}),
      },
      body: JSON.stringify({
        title,
        pages,
        zoom,
        policyId,
        footer,
        includeCoverToc,
        // Generation page shows content only — exclude cover + TOC pages and logo.
        markdown: contentPagesToMarkdown(pages as { plateValue?: unknown }[]),
      }),
    });
    if (!resp.ok) throw new Error(`Save failed: ${resp.status}`);
    return resp.json();
  },
  // Auto-persist regenerated markdown after a whole-document regen in the editor.
  // Updates ONLY the policy content: policy_markdown_url in the DB (via the
  // content PATCH endpoint — no editor JSON, step, or status change) and the
  // generation-page session cache (sections + fullContent only), so navigating
  // back to the generation page shows the regenerated text.
  onDocumentRegenerated: async ({ policyId, markdown, userId, orgId, title }) => {
    // Refresh the same-tab session cache first — works even for unsaved drafts.
    try {
      sessionStorage.setItem(
        "revisr_brand_policy_result",
        mergeSessionResultContent(
          sessionStorage.getItem("revisr_brand_policy_result"),
          markdown,
          title || "Policy",
        ),
      );
    } catch { /* non-fatal */ }

    // Persist to the DB when a record exists (drafts have no row yet).
    const resolvedPolicyId =
      policyId || sessionStorage.getItem("boost_policy_record_id") || undefined;
    if (!resolvedPolicyId) return;
    const resolvedOrgId = orgId ?? getActiveWorkspaceId() ?? undefined;
    await saveContent(resolvedPolicyId, markdown, userId, resolvedOrgId);
  },
  onNavigateToPreview: async ({ policyId, pages, zoom, userId, title, footer, includeCoverToc }) => {
    const apiUrl = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || "";
    const orgId = getActiveWorkspaceId() ?? undefined;
    const orgHeader = orgId ? { "x-org-id": orgId } : {};
    // Save current editor content (including title so backend doesn't reject it)
    const saveResp = await fetch(`${apiUrl}/api/revisr/brand-policy/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "revisr",
        "x-user-id": userId,
        ...orgHeader,
      },
      body: JSON.stringify({
        pages,
        zoom,
        policyId,
        title,
        footer,
        includeCoverToc,
        // Generation page shows content only — exclude cover + TOC pages and logo.
        markdown: contentPagesToMarkdown(pages as { plateValue?: unknown }[]),
      }),
    });
    if (!saveResp.ok) {
      console.warn("[onNavigateToPreview] Content save failed:", saveResp.status);
    }
    // Update step to reflect the user has progressed to preview
    const stepResp = await fetch(`${apiUrl}/api/revisr/brand-policy/${policyId}/step`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "revisr",
        "x-user-id": userId,
        ...orgHeader,
      },
      body: JSON.stringify({ current_step: 6 }),
    });
    if (!stepResp.ok) {
      console.warn("[onNavigateToPreview] Step update failed:", stepResp.status);
    }
  },
};

// Config for the Preview & Finalise page — same as editor config but:
// - Back button goes to editor (not generating)
// - No routes.preview → shows "Save Policy" button instead of "Preview & Finalise"
const REVISR_BRAND_PREVIEW_CONFIG: BrandWorkflowConfig = {
  ...REVISR_BRAND_CONFIG,
  routes: {
    ...REVISR_BRAND_CONFIG.routes,
    brandForm: "/revisr/brand-policy/editor",  // Back navigates to editor
    preview: undefined,                         // No preview route → shows Save button
  },
  feedback: { enabled: true },
};

// Define routes for the Revisr plugin
const routes: RouteObject[] = [
  // Dashboard routes
  {
    path: "/revisr",
    element: React.createElement(Dashboard),
  },
  {
    path: "/revisr/dashboard",
    element: React.createElement(Dashboard),
  },
  // Post-onboarding choice (after wizard completion)
  {
    path: "/revisr/get-started",
    element: React.createElement(PostOnboardingChoice),
  },
  // Organisation Profile page
  {
    path: "/revisr/organisation-profile",
    element: React.createElement(OrganisationProfilePage),
  },
  // Org Memory Manager
  {
    path: "/revisr/org-memory",
    element: React.createElement(OrgMemoryManagerPage),
  },
  // Policy Documents routes
  {
    path: "/revisr/policies",
    element: React.createElement(PolicyDocuments),
  },
  {
    path: "/revisr/policies/generated",
    element: React.createElement(PreviewExport),
  },
  // Template Library route
  {
    path: "/revisr/revisr-library",
    element: React.createElement(TemplateLibrary),
  },
  // Audit Workflow routes
  {
    path: "/revisr/new-audit",
    element: React.createElement(NewAudit),
  },
  {
    path: "/revisr/audit-workflow-selection",
    element: React.createElement(AuditWorkflowSelection),
  },
  {
    path: "/revisr/boost-policy",
    element: React.createElement(BoostPolicyFlow),
  },
  // Policy Templates routes (ScopingAI pages with Revisr config)
  {
    path: "/revisr/templates",
    element: React.createElement(DocumentsLibrary, { config: REVISR_TEMPLATES_CONFIG }),
  },
  {
    path: "/revisr/templates/create",
    element: React.createElement(TemplateCreation, { config: REVISR_TEMPLATE_EDITOR_CONFIG }),
  },
  {
    path: "/revisr/template-editor/new",
    element: React.createElement(ProposalTemplateEditor, { config: REVISR_TEMPLATE_EDITOR_CONFIG }),
  },
  {
    path: "/revisr/template-editor/:id",
    element: React.createElement(ProposalTemplateEditor, { config: REVISR_TEMPLATE_EDITOR_CONFIG }),
  },
  // Brand Your Policy workflow routes (ScopingAI pages with Revisr config)
  // Org Admin Portal — accessible to org_admin users (Policy Templates + AI Skills)
  {
    path: "/org-admin",
    element: React.createElement(OrgAdminPortal),
  },
  // Policy Approvals - org admins review member-submitted policies (approval workflow).
  // Gated by VITE_POLICY_APPROVAL_REQUIRED; the sidebar entry only shows for org
  // admins when the flag is on. Wrapped in RevisrLayout so the sidebar persists.
  {
    path: "/revisr/policy-approvals",
    element: React.createElement(RevisrLayout, null, React.createElement(PolicyApprovals)),
  },
  {
    path: "/revisr/brand-policy",
    element: React.createElement(BrandYourProposal, { config: REVISR_BRAND_CONFIG }),
  },
  {
    path: "/revisr/brand-policy/generating",
    element: React.createElement(BoostPolicyGenerating),
  },
  {
    path: "/revisr/brand-policy/editor",
    element: React.createElement(BrandPolicyEditorWrapper, { keyProp: "revisr-brand-editor", config: REVISR_BRAND_CONFIG }),
  },
  {
    path: "/revisr/brand-policy/preview",
    element: React.createElement(BrandPolicyEditorWrapper, { keyProp: "revisr-brand-preview", config: REVISR_BRAND_PREVIEW_CONFIG }),
  },
  {
    path: "/revisr/scan-review",
    element: React.createElement(ScanAndReview),
  },
  {
    path: "/revisr/edit-approve",
    element: React.createElement(EditAndApprove),
  },
  {
    path: "/revisr/preview-export",
    element: React.createElement(PreviewExport),
  },
  // Create Policy (Policy Generation) routes
  {
    path: "/revisr/create-policy",
    element: React.createElement(PolicyDetailsPage),
  },
  {
    path: "/revisr/create-policy/generated",
    element: React.createElement(PolicyGeneratePage),
  },
  {
    path: "/revisr/create-policy/edit",
    element: React.createElement(PolicyEditPage),
  },
  {
    path: "/revisr/create-policy/export",
    element: React.createElement(PreviewExport),
  },
  // Document Register (coming soon preview)
  {
    path: "/revisr/document-register",
    element: React.createElement(DocumentRegister),
  },
  // Quick Compare route
  {
    path: "/revisr/quick-compare",
    element: React.createElement(QuickCompare),
  },
  // Knowledge Base / Resource Library routes (specific paths before parent for correct React Router matching)
  {
    path: "/revisr/resource-library/documents",
    element: React.createElement(KnowledgeBaseDocuments),
  },
  {
    path: "/revisr/resource-library/prompts",
    element: React.createElement(KnowledgeBasePrompts),
  },
  {
    path: "/revisr/resource-library",
    element: React.createElement(KnowledgeBase),
  },
  // ParseDoc route
  {
    path: "/revisr/parsedoc",
    element: React.createElement(ParseDoc),
  },
  // Admin: Context Categories management (super-admin only)
  {
    path: "/revisr/admin/context-categories",
    element: React.createElement(RevisrLayout, null, React.createElement(ContextCategoriesPage)),
  },
  {
    path: "/revisr/admin/context-categories/:id",
    element: React.createElement(ContextCategoryEditPage),
  },
  // Document Regeneration route - TEMP: Disabled - depends on deleted components
  // {
  //   path: "/revisr/documents/regeneration",
  //   element: React.createElement(DocumentsRegeneration),
  // },
];

// Define navigation items - simple flat structure like pseo and crm
const navigation: NavigationItem[] = [
  {
    name: "Home",
    route: "/revisr",
    icon: "FileText",
    order: 7,
    permissions: ["revisr:read"],
  },
  // DEACTIVATED: Quick Compare - to re-enable, uncomment below
  // {
  //   name: "Quick Compare",
  //   route: "/revisr/quick-compare",
  //   icon: "GitCompare",
  //   order: 8,
  //   permissions: ["revisr:read"],
  // },
  // {
  //   name: "Parse Doc",
  //   route: "/revisr/parsedoc",
  //   icon: "FileCode",
  //   order: 9,
  //   permissions: ["revisr:read"],
  // },
];

// Revisr plugin module
const revisrPlugin: PluginModule = {
  routes,
  navigation,
  providers: [], // No specific providers for Revisr yet
  config: {
    name: "Revisr",
    version: "1.0.0",
    description:
      "AI-powered project scoping and proposal generation system with document management and knowledge base",
  },
  initialize: async () => {
    console.log("🎯 Revisr plugin initialized");
  },
  cleanup: async () => {
    console.log("🎯 Revisr plugin cleaned up");
  },
};

export default revisrPlugin;
